import { useQuery, UseQueryResult } from 'react-query';
import { getDocumentScan, getDocumentScans } from 'src/api/documentScans';
import { Encounter, FilterQueryParams } from 'src/utils/types';

export const documentScansKey = ['documentScans'] as const;

export interface DocumentScan {
    documentScanId: string;
    aiPromptId: string | null;
    aiPromptAuditVersion: number | null;
    aiPromptContextId: string | null;
    aiPromptContextAuditVersion: number | null;
    documentScanTypeId?: string | null;
    documentScanTypeVersionId?: string | null;
    facilityDocumentScanTypeId?: string | null;
    facilityDocumentScanTypeVersionId?: string | null;
    facId: number;
    scanMetadata: Record<string, any>;
    scanStatus?: string | null;
    extractedData?: Record<string, any> | null;
    archived?: boolean;
    archivedAt?: Date | null;
    archivedByUserId?: number | null;
    archivedReason?: string | null;
    createdAt?: Date;
    lastUpdatedAt?: Date;
    auditVersion: number;
    assets?: Array<{
        documentScanAssetId: string;
        documentScanId: string;
        assetType: string;
        assetUri: string;
        status: string;
        errorMessage: string | null;
        assetMetadata?: Record<string, any>;
        createdAt?: Date;
        lastUpdatedAt?: Date;
        auditVersion: number;
        viewUrl?: string | null;
        content?: string | any | null;
    }>;
    entities?: Array<{
        documentScanEntityId?: string;
        entityType: string;
        entityIdentifier: string;
        relationshipType?: string | null;
        relationshipMetadata?: Record<string, any> | null;
    }>;
    documentScanType?: {
        scanTypeKey?: string;
        baseScanTypeKey?: string | null;
        supportedEntityType?: string;
    } | null;
    documentScanTypeVersion?: {
        scanTypeName?: string;
        scanTypeDescription?: string | null;
        captureMode?: string;
        captureConfig?: Record<string, any>;
    } | null;
    facilityDocumentScanTypeVersion?: {
        enabled?: boolean;
        overrides?: Record<string, any>;
    } | null;
    encounter?: Encounter | null;
}

export interface DocumentScansResponse {
    result: DocumentScan[];
    count: number;
}

export interface UseDocumentScansResult {
    data: DocumentScan[];
    count: number;
}

export const useDocumentScans = (
    facilityId: number,
    orgId: number | undefined,
    params?: FilterQueryParams & { hasEncounter?: boolean; encounterNumber?: string },
): UseQueryResult<UseDocumentScansResult> => {
    return useQuery({
        queryKey: [
            ...documentScansKey,
            orgId,
            facilityId,
            params?.hasEncounter,
            params?.encounterNumber,
            params?.pagination,
        ],
        queryFn: async () => {
            const response = await getDocumentScans(facilityId, params);
            // Parse date strings to Date objects
            // response.result is actually an array despite the type definition
            const resultArray = (response.result as any) || [];
            const data = (Array.isArray(resultArray) ? resultArray : []).map((scan: any) => {
                return {
                    ...scan,
                    createdAt: scan.createdAt ? new Date(scan.createdAt as any) : undefined,
                    lastUpdatedAt: scan.lastUpdatedAt ? new Date(scan.lastUpdatedAt as any) : undefined,
                    archivedAt: scan.archivedAt ? new Date(scan.archivedAt as any) : null,
                    assets: scan.assets?.map((asset: DocumentScan['assets'][0]) => ({
                        ...asset,
                        createdAt: asset.createdAt ? new Date(asset.createdAt as any) : undefined,
                        lastUpdatedAt: asset.lastUpdatedAt ? new Date(asset.lastUpdatedAt as any) : undefined,
                    })),
                };
            });
            return {
                data,
                count: response.count,
            };
        },
        enabled: !!facilityId, // Only run query if facilityId is provided
    });
};

export const useDocumentScan = (documentScanId: string | undefined): UseQueryResult<DocumentScan> => {
    return useQuery({
        queryKey: [...documentScansKey, documentScanId],
        queryFn: async () => {
            const response = await getDocumentScan(documentScanId!);
            // Parse date strings to Date objects
            return {
                ...response,
                createdAt: response.createdAt ? new Date(response.createdAt as any) : undefined,
                lastUpdatedAt: response.lastUpdatedAt ? new Date(response.lastUpdatedAt as any) : undefined,
                archivedAt: response.archivedAt ? new Date(response.archivedAt as any) : null,
                assets: response.assets?.map((asset: DocumentScan['assets'][0]) => ({
                    ...asset,
                    createdAt: asset.createdAt ? new Date(asset.createdAt as any) : undefined,
                    lastUpdatedAt: asset.lastUpdatedAt ? new Date(asset.lastUpdatedAt as any) : undefined,
                })),
            };
        },
        enabled: !!documentScanId,
    });
};
